/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { ReactElement, useState } from "react";
import { VisState } from "../../helpers/vis";
import "./AddEventForm.css";
import OpenColor from "open-color";
import t from "../../helpers/i18n";
import { rawWriteRegister } from "../../helpers/params";
import { Device } from "../../helpers/org-struct";

interface FormData {
  isSubmitting: boolean;
  days: number[];
  startPeriodHours: number;
  startPeriodMinutes: number;
  endPeriodHours: number;
  endPeriodMinutes: number;
}

export default function AddEventForm({
  vis,
  device,
  showSpinner,
  registers,
}: {
  vis: VisState;
  device: Device;
  showSpinner: (show: boolean) => void;
  registers: Readonly<Readonly<[key: string, addr: string, updatePerSec: number]>[]>;
}): ReactElement {
  const [formData, setFormData] = useState<FormData>({
    isSubmitting: false,
    days: [0],
    startPeriodHours: 0,
    startPeriodMinutes: 0,
    endPeriodHours: 0,
    endPeriodMinutes: 0,
  });

  return (
    <div className="settings-container">
      <form
        className="settings-form"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={async (e) => {
          e.preventDefault();
          showSpinner(true);
          setFormData({ ...formData, isSubmitting: true });
          await submitForm(formData, vis, device.netId, device.modbusUnitId, registers);
          setFormData({
            ...formData,
            isSubmitting: false,
            days: [0],
            startPeriodHours: 0,
            startPeriodMinutes: 0,
            endPeriodHours: 0,
            endPeriodMinutes: 0,
          });
          showSpinner(false);
        }}
      >
        <div className="days-checkboxes-list">
          {[
            t("MN", "ПН"),
            t("TU", "ВТ"),
            t("WD", "СР"),
            t("TH", "ЧТ"),
            t("FR", "ПТ"),
            t("SA", "СБ"),
            t("SU", "ВС"),
          ].map((d, i) => (
            <div className="day-container" key={i}>
              <input
                type="checkbox"
                id={`day-${i}-checkbox`}
                checked={formData.days.includes(i)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({ ...formData, days: [...formData.days, i] });
                  } else {
                    setFormData({ ...formData, days: formData.days.filter((d) => d !== i) });
                  }
                }}
              />
              <label htmlFor={`day-${i}-checkbox`}>{d}</label>
            </div>
          ))}
        </div>
        <div className="time-container">
          <div style={{ overflow: "hidden" }}>
            <span style={{ color: OpenColor.green[7], marginRight: "5px" }}>
              <i className="fas fa-lightbulb"></i>
            </span>
            <input
              type="time"
              value={`${formData.startPeriodHours
                .toFixed()
                .padStart(2, "0")}:${formData.startPeriodMinutes.toFixed().padStart(2, "0")}`}
              required
              onChange={(e) => {
                const [hours, minutes] = e.target.value.split(":");
                setFormData({
                  ...formData,
                  startPeriodHours: Number(hours),
                  startPeriodMinutes: Number(minutes),
                });
              }}
            />
          </div>

          <div className="dash">—</div>

          <div style={{ marginRight: "30px", overflow: "hidden" }}>
            <span style={{ color: OpenColor.gray[6], marginRight: "5px" }}>
              <i className="fas fa-lightbulb"></i>
            </span>
            <input
              type="time"
              value={`${formData.endPeriodHours
                .toFixed()
                .padStart(2, "0")}:${formData.endPeriodMinutes.toFixed().padStart(2, "0")}`}
              required
              onChange={(e) => {
                const [hours, minutes] = e.target.value.split(":");
                setFormData({
                  ...formData,
                  endPeriodHours: Number(hours),
                  endPeriodMinutes: Number(minutes),
                });
              }}
            />
          </div>
        </div>

        <button
          className="btn btn-primary"
          type="submit"
          disabled={formData.isSubmitting || !formData.days.length}
        >
          {formData.isSubmitting ? t("Saving...", "Сохранение...") : t("Add event", "Добавить")}
        </button>
      </form>
    </div>
  );
}

async function submitForm(
  formData: FormData,
  vis: VisState,
  netId: number,
  modbusUnitId: number,
  registers: Readonly<Readonly<[key: string, addr: string, updatePerSec: number]>[]>,
): Promise<void> {
  const keys = registers.map((r) => r[0]);
  for (const day of formData.days) {
    const startPeriodKey = keys.find((k) => k.includes("Start") && Number(vis.data.get(k)) === 0);
    if (!startPeriodKey) {
      alert(
        t(
          "Can't add new event since the schedule is full or data hasn't loaded properly",
          "Невозможно добавить новое событие, так как расписание полностью заполнено или данные не загружены",
        ),
      );
      return;
    }
    const endPeriodKey = startPeriodKey.replace("Start", "End");

    const startMinutes =
      day * 1440 + formData.startPeriodHours * 60 + formData.startPeriodMinutes + 1;

    const endMinutes =
      formData.endPeriodHours === 0 && formData.endPeriodMinutes === 0
        ? day * 1440 + 1440
        : day * 1440 + formData.endPeriodHours * 60 + formData.endPeriodMinutes;

    if (!startMinutes || !endMinutes || startMinutes >= endMinutes) {
      alert("Invalid period");
      return;
    }
    await vis.params.rawWriteRegister(startPeriodKey, [startMinutes, endMinutes]);
    await vis.params.readNowFromDevice(endPeriodKey);
  }
  await rawWriteRegister(netId, modbusUnitId, 120, "holding", [4]);
}
